import React, { useState, useEffect } from 'react';
import { Offcanvas, Nav, Form, InputGroup, Button, Badge, ListGroup, Accordion } from 'react-bootstrap';
import { FaSearch, FaBars, FaTimes, FaUser, FaFileAlt, FaPlus, FaClock, FaCheck } from 'react-icons/fa';
import './MobileNavigationDrawer.css';

const MobileNavigationDrawer = ({
    show,
    onHide,
    folders = [],
    forms = [],
    onFolderSelect,
    onFormSelect,
    searchTerm,
    onSearchChange,
    recentForms = [],
    patient = null,
    selectedFolder = 'all'
}) => {
    const [searchResults, setSearchResults] = useState([]);
    const [activeAccordion, setActiveAccordion] = useState('0');

    // Clinical folder categories optimized for mobile
    const clinicalFolders = [
        { id: 'assessment', name: 'Assessment', icon: '🏥', color: 'success' },
        { id: 'orders', name: 'Orders', icon: '💊', color: 'warning' },
        { id: 'documentation', name: 'Documentation', icon: '📋', color: 'info' },
        { id: 'monitoring', name: 'Monitoring', icon: '👨‍⚕️', color: 'primary' },
        { id: 'administrative', name: 'Administrative', icon: '📄', color: 'secondary' }
    ];

    // Map actual folders to clinical categories
    const getClinicalFolder = (folderId) => {
        if (folderId === 'all') return { id: 'all', name: 'All Forms', icon: '📋', color: 'primary' };
        
        const folder = folders.find(f => f.folder_id === folderId);
        if (!folder) return clinicalFolders[0];

        const folderName = folder.name.toLowerCase();
        
        if (folderName.includes('assessment') || folderName.includes('admission')) {
            return clinicalFolders[0];
        } else if (folderName.includes('order') || folderName.includes('treatment') || folderName.includes('procedure')) {
            return clinicalFolders[1];
        } else if (folderName.includes('documentation') || folderName.includes('daily') || folderName.includes('note')) {
            return clinicalFolders[2];
        } else if (folderName.includes('monitoring') || folderName.includes('vital') || folderName.includes('track')) {
            return clinicalFolders[3];
        } else if (folderName.includes('administrative') || folderName.includes('insurance') || folderName.includes('discharge')) {
            return clinicalFolders[4];
        }
        
        return { ...folder, icon: '📋', color: 'info' };
    };

    // Group forms by clinical folders
    const getFormsForClinicalFolder = (clinicalFolderId) => {
        if (clinicalFolderId === 'all') return forms;
        
        return forms.filter(form => {
            const folder = folders.find(f => f.folder_id === form.folder_id);
            if (!folder) return false;
            
            const folderName = folder.name.toLowerCase();
            
            switch (clinicalFolderId) {
                case 'assessment':
                    return folderName.includes('assessment') || folderName.includes('admission');
                case 'orders':
                    return folderName.includes('order') || folderName.includes('treatment') || folderName.includes('procedure');
                case 'documentation':
                    return folderName.includes('documentation') || folderName.includes('daily') || folderName.includes('note');
                case 'monitoring':
                    return folderName.includes('monitoring') || folderName.includes('vital') || folderName.includes('track');
                case 'administrative':
                    return folderName.includes('administrative') || folderName.includes('insurance') || folderName.includes('discharge');
                default:
                    return false;
            }
        });
    };

    // Handle search functionality
    useEffect(() => {
        if (searchTerm && searchTerm.length > 0) {
            const results = forms.filter(form =>
                (form.name?.toLowerCase() || form.formName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                form.description?.toLowerCase().includes(searchTerm.toLowerCase())
            ).slice(0, 20); // Show more results on mobile
            setSearchResults(results);
        } else {
            setSearchResults([]);
        }
    }, [searchTerm, forms]);

    const handleFormSelect = (form) => {
        onFormSelect(form);
        onHide(); // Close drawer after selection
    };

    const handleFolderSelect = (folderId) => {
        onFolderSelect(folderId);
        onHide(); // Close drawer after selection
    };

    return (
        <Offcanvas show={show} onHide={onHide} placement="start" className="mobile-navigation-drawer">
            <Offcanvas.Header closeButton className="border-bottom">
                <Offcanvas.Title>
                    <div className="d-flex align-items-center">
                        <FaBars className="me-2 text-primary" />
                        Form Navigation
                    </div>
                </Offcanvas.Title>
            </Offcanvas.Header>
            
            <Offcanvas.Body className="p-0">
                {/* Patient Info (if available) */}
                {patient && (
                    <div className="patient-info-mobile bg-primary text-white p-3 mb-3">
                        <div className="d-flex align-items-center">
                            <div className="patient-avatar-mobile me-3">
                                <FaUser />
                            </div>
                            <div>
                                <div className="fw-bold">
                                    {patient.full_name}
                                </div>
                                <small className="text-light">
                                    MRN: {patient.mrn || patient.id}
                                </small>
                                {patient.age && (
                                    <div>
                                        <small className="text-light">
                                            Age: {patient.age}
                                        </small>
                                    </div>
                                )}
                            </div>
                        </div>
                        <Badge bg="light" text="dark" className="mt-2">
                            {patient.care_status?.status || patient.status || 'Active'}
                        </Badge>
                    </div>
                )}

                {/* Search */}
                <div className="p-3 border-bottom">
                    <InputGroup className="mobile-search">
                        <InputGroup.Text>
                            <FaSearch />
                        </InputGroup.Text>
                        <Form.Control
                            type="text"
                            placeholder="Search forms..."
                            value={searchTerm}
                            onChange={(e) => onSearchChange(e.target.value)}
                            className="mobile-search-input"
                        />
                        {searchTerm && (
                            <Button
                                variant="outline-secondary"
                                onClick={() => onSearchChange('')}
                            >
                                <FaTimes />
                            </Button>
                        )}
                    </InputGroup>

                    {/* Search Results */}
                    {searchResults.length > 0 && (
                        <div className="search-results-mobile mt-3">
                            <div className="fw-bold mb-2">Search Results</div>
                            <ListGroup>
                                {searchResults.map((form, index) => (
                                    <ListGroup.Item
                                        key={index}
                                        action
                                        onClick={() => handleFormSelect(form)}
                                        className="mobile-form-item"
                                    >
                                        <div>
                                            <div className="fw-bold">{form.name || form.formName}</div>
                                            {form.description && (
                                                <small className="text-muted">
                                                    {form.description.substring(0, 60)}...
                                                </small>
                                            )}
                                        </div>
                                        <FaPlus className="text-primary" />
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        </div>
                    )}
                </div>

                {/* Navigation Sections */}
                {!searchTerm && (
                    <Accordion defaultActiveKey="0" className="mobile-navigation-accordion">
                        {/* Recent Forms */}
                        {recentForms.length > 0 && (
                            <Accordion.Item eventKey="recent">
                                <Accordion.Header>
                                    <div className="d-flex align-items-center justify-content-between w-100 me-3">
                                        <span>
                                            <FaClock className="me-2 text-info" />
                                            Recent Forms
                                        </span>
                                        <Badge bg="info">{recentForms.length}</Badge>
                                    </div>
                                </Accordion.Header>
                                <Accordion.Body className="p-0">
                                    <ListGroup variant="flush">
                                        {recentForms.slice(0, 8).map((form, index) => (
                                            <ListGroup.Item
                                                key={index}
                                                action
                                                onClick={() => handleFormSelect(form)}
                                                className="mobile-form-item"
                                            >
                                                <div>
                                                    <div className="fw-bold">{form.name || form.formName}</div>
                                                    <small className="text-muted">
                                                        {form.lastUsed ? new Date(form.lastUsed).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'Recently used'}
                                                    </small>
                                                </div>
                                                <FaPlus className="text-primary" />
                                            </ListGroup.Item>
                                        ))}
                                    </ListGroup>
                                </Accordion.Body>
                            </Accordion.Item>
                        )}

                        {/* Clinical Folders */}
                        {clinicalFolders.map((folder, index) => {
                            const folderForms = getFormsForClinicalFolder(folder.id);
                            
                            return (
                                <Accordion.Item key={folder.id} eventKey={index.toString()}>
                                    <Accordion.Header>
                                        <div className="d-flex align-items-center justify-content-between w-100 me-3">
                                            <span>
                                                <span className="me-2" style={{ fontSize: '1.2rem' }}>
                                                    {folder.icon}
                                                </span>
                                                {folder.name}
                                            </span>
                                            <Badge bg={folder.color}>{folderForms.length}</Badge>
                                        </div>
                                    </Accordion.Header>
                                    <Accordion.Body className="p-0">
                                        {folderForms.length > 0 ? (
                                            <>
                                                <div className="p-3 bg-light border-bottom">
                                                    <Button
                                                        variant={`outline-${folder.color}`}
                                                        size="sm"
                                                        onClick={() => handleFolderSelect(folder.id)}
                                                        className="w-100"
                                                    >
                                                        View All {folderForms.length} Forms
                                                    </Button>
                                                </div>
                                                <ListGroup variant="flush">
                                                    {folderForms.slice(0, 10).map((form, formIndex) => (
                                                        <ListGroup.Item
                                                            key={formIndex}
                                                            action
                                                            onClick={() => handleFormSelect(form)}
                                                            className="mobile-form-item"
                                                        >
                                                            <div>
                                                                <div className="fw-bold">{form.name || form.formName}</div>
                                                                {form.description && (
                                                                    <small className="text-muted">
                                                                        {form.description.substring(0, 50)}...
                                                                    </small>
                                                                )}
                                                            </div>
                                                            <FaPlus className="text-primary" />
                                                        </ListGroup.Item>
                                                    ))}
                                                    {folderForms.length > 10 && (
                                                        <ListGroup.Item className="text-center text-muted">
                                                            <small>
                                                                +{folderForms.length - 10} more forms...
                                                            </small>
                                                        </ListGroup.Item>
                                                    )}
                                                </ListGroup>
                                            </>
                                        ) : (
                                            <div className="p-3 text-center text-muted">
                                                <small>No forms in this category</small>
                                            </div>
                                        )}
                                    </Accordion.Body>
                                </Accordion.Item>
                            );
                        })}
                    </Accordion>
                )}
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default MobileNavigationDrawer;